import { AlertCircle, FileText, FileType, Image as ImageIcon, Loader2, X } from "lucide-react";
import * as React from "react";
import { cn } from "src/common/utils";

import type { ChatAttachmentType } from "./useChatAttachments";

interface ChatAttachmentChipsProps {
  attachments: ChatAttachmentType[];
  onRemove: (id: string) => void;
  className?: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const FileIcon: React.FC<{ mimetype: string }> = ({ mimetype }) => {
  if (mimetype.startsWith("image/")) return <ImageIcon className="h-5 w-5 text-muted-foreground" />;
  if (mimetype === "application/pdf") return <FileType className="h-5 w-5 text-muted-foreground" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
};

export const ChatAttachmentChips: React.FC<ChatAttachmentChipsProps> = ({ attachments, onRemove, className }) => {
  if (attachments.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-2 px-2 pt-2 pb-1", className)}>
      {attachments.map((a) => {
        const isError = a.status === "error";
        const isUploading = a.status === "uploading";
        return (
          <div
            key={a.id}
            className={cn(
              "group relative flex items-center gap-2 rounded-md border bg-background pl-1 pr-7 py-1 max-w-[240px] text-xs",
              isError ? "border-destructive/60" : "border-border"
            )}
            title={isError ? a.errorMessage : a.file.name}
          >
            <div className="flex-shrink-0 h-8 w-8 rounded overflow-hidden bg-muted flex items-center justify-center">
              {isError ? (
                <AlertCircle className="h-5 w-5 text-destructive" />
              ) : isUploading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : a.previewUrl ? (
                <img src={a.previewUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <FileIcon mimetype={a.file.type} />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="truncate text-foreground">{a.file.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {isError ? a.errorMessage : formatSize(a.file.size)}
              </span>
            </div>
            <button
              type="button"
              aria-label={`Remove ${a.file.name}`}
              onClick={() => onRemove(a.id)}
              className="absolute right-1 top-1 h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
